/** Sparse native sandstone monuments. Convex table blocks share one physical
 * heightfield; their union has broken caps, planar faces and connected talus. */
import {hashSeed,rngFromString} from './rng.js';
import * as pmath from './pmath.js';
import {duneClimate,duneDirection,type DuneDef,type DuneNativeSample} from './dunes.js';
import type {PlanetDef} from './universe.js';

type V3=readonly[number,number,number];
export interface ButtePlane {
 readonly x:number;readonly y:number;readonly distanceM:number;
 readonly insetM:number;readonly kneeM:number;readonly kneeHeightM:number;
 readonly setbackM:number;readonly lowerM:number;readonly shoulderM:number;readonly apronM:number;
}
export interface ButteBlock {
 readonly u:number;readonly v:number;readonly heightM:number;
 readonly faceM:number;readonly shoulderM:number;readonly apronM:number;
 readonly shoulderHeightM:number;readonly planes:readonly ButtePlane[];
}
export interface ButteDef {
 readonly id:string;readonly seed:number;readonly center:V3;readonly along:V3;readonly across:V3;
 /** Conservative finite bounds for spatial queries and construction-time LOD. */
 readonly radiusM:number;readonly heightM:number;
 readonly baseM:number;readonly gradeU:number;readonly gradeV:number;
 readonly blocks:readonly ButteBlock[];readonly duneId:string|null;
}
export interface ButteSample {heightM:number;rock:number;weight:number}
export const butteSample=():ButteSample=>({heightM:0,rock:0,weight:0});
const smooth=(v:number):number=>{const t=Math.max(0,Math.min(1,v));return t*t*(3-2*t)};
const norm=(x:number,y:number,z:number):V3=>{const l=Math.sqrt(x*x+y*y+z*z);return[x/l,y/l,z/l]};
const dot=(a:V3,b:V3):number=>a[0]*b[0]+a[1]*b[1]+a[2]*b[2];
const cross=(a:V3,b:V3):V3=>[a[1]*b[2]-a[2]*b[1],a[2]*b[0]-a[0]*b[2],a[0]*b[1]-a[1]*b[0]];
export const butteDirection=duneDirection;
/** Linear sandstone planes with short rounded joins, not sinusoidal steps. */
function ramp(v:number):number {
 const t=Math.max(0,Math.min(1,v)),e=.045;
 if(t<e)return t*t/(2*e*(1-e));if(t>1-e)return 1-(1-t)*(1-t)/(2*e*(1-e));return(t-e*.5)/(1-e);
}
/** Absolute rock surface before native climate and downstream water edits.
 * max() preserves a continuous union and angular ridges between table blocks. */
export function butteProfile(f:ButteDef,u:number,v:number,out:ButteSample):ButteSample {
 let rise=0;
 if(u*u+v*v<f.radiusM*f.radiusM)for(const b of f.blocks){
  const x=u-b.u,y=v-b.v;let block=b.heightM;
  for(const p of b.planes){
   const edge=x*p.x+y*p.y-p.distanceM,shoulderEnd=p.lowerM+p.shoulderM,outer=shoulderEnd+p.apronM;
   if(edge>=outer){block=0;break;}
   let h:number;
   if(edge<=-p.insetM)continue;
   // Each exposed side retreats differently and changes plane at its own
   // broad fracture height. The short setbacks therefore end at corners;
   // they do not form equally wide horizontal rings around the monument.
   if(edge<p.kneeM)h=b.heightM-(b.heightM-p.kneeHeightM)*ramp((edge+p.insetM)/(p.kneeM+p.insetM));
   else if(edge<p.kneeM+p.setbackM)h=p.kneeHeightM-(edge-p.kneeM)*.18;
   else if(edge<p.lowerM){
    const top=p.kneeHeightM-p.setbackM*.18;
    h=top-(top-b.shoulderHeightM)*ramp((edge-p.kneeM-p.setbackM)/(p.lowerM-p.kneeM-p.setbackM));
   }else if(edge<shoulderEnd)h=b.shoulderHeightM-(edge-p.lowerM)*.055;
   else h=(b.shoulderHeightM-p.shoulderM*.055)*(1-ramp((edge-shoulderEnd)/p.apronM));
   block=Math.min(block,h);if(block<=rise)break;
  }
  if(block>rise)rise=block;
 }
 out.heightM=f.baseM+f.gradeU*u+f.gradeV*v+rise;
 out.weight=smooth(rise/12);out.rock=smooth(rise/18);return out;
}

export class ButteTerrain {
 readonly enabled:boolean;
 readonly features:readonly ButteDef[];
 private readonly cells=new Map<number,readonly ButteDef[]>();
 private readonly cellM:number;
 private readonly gridSize:number;
 private readonly seaM:number;
 private readonly scratch=butteSample();
 constructor(private readonly def:PlanetDef,dunes:readonly DuneDef[],native:(x:number,y:number,z:number,out:DuneNativeSample)=>DuneNativeSample){
  this.enabled=def.terrain.amplitudeRel>=.001&&def.atmosphere!==null&&
   (def.archetype==='desert'||((def.archetype==='terran'||def.archetype==='ocean')&&def.hasFlora));
  this.cellM=Math.min(1100,def.radius*.12);this.gridSize=Math.ceil(2*def.radius/this.cellM)+3;
  this.seaM=def.seaRadius===null?-1e6:def.seaRadius-def.radius;
  const features:ButteDef[]=[];this.features=features;
  if(!this.enabled){Object.freeze(features);return;}
  const scratch:DuneNativeSample={heightM:0,temp:0,moist:0,mountain:0,plateau:0};
  const scale=Math.min(1,def.radius/12000),limit=def.archetype==='desert'?16:10;
  const tryFeature=(center:V3,key:string,duneId:string|null)=>{
   if(features.length>=limit)return;
   const s=native(...center,scratch);
   if(duneClimate(s.temp,s.moist)<.35||s.heightM<this.seaM+55||s.mountain>.12||s.plateau>.025)return;
   const baseM=s.heightM,random=rngFromString(`${def.seed}:butte-shape:v1:${key}`),seed=hashSeed(`${def.seed}:butte:${key}`);
   const east=norm(center[2],0,-center[0]),north=cross(center,east),angle=random()*pmath.TWO_PI,c=pmath.cos(angle),q=pmath.sin(angle);
   const along:V3=[east[0]*c+north[0]*q,east[1]*c+north[1]*q,east[2]*c+north[2]*q],across=cross(along,center);
   const frame={center,along,across};let gradeU=0,gradeV=0;
   const probe=Math.max(30,120*scale);
   for(let axis=0;axis<2;axis++){
    const a=native(...butteDirection(frame,def.radius,axis===0?-probe:0,axis===1?-probe:0),scratch).heightM;
    const b=native(...butteDirection(frame,def.radius,axis===0?probe:0,axis===1?probe:0),scratch).heightM;
    if(Math.abs(a-baseM)>probe*.20||Math.abs(b-baseM)>probe*.20)return;
    const grade=Math.max(-.08,Math.min(.08,(b-a)/(probe*2)));if(axis===0)gradeU=grade;else gradeV=grade;
   }
   const blocks:ButteBlock[]=[],height=(165+random()*115)*scale,count=3+Math.floor(random()*3);let radiusM=0;
   for(let j=0;j<count;j++){
    const u=j===0?0:(random()-.5)*250*scale,v=j===0?0:(random()-.5)*210*scale;
    const h=height*(j===0?1:.40+random()*.56),r=(j===0?65+random()*35:32+random()*40)*scale;
    const faceM=Math.max(14*scale,h*(.16+random()*.055)),shoulderM=(14+random()*22)*scale;
    const shoulderHeightM=h*(.19+random()*.09),apronM=Math.max(35*scale,shoulderHeightM/(.46+random()*.14));
    const planes:ButtePlane[]=[],sides=5+Math.floor(random()*3),phase=random()*pmath.TWO_PI;
    let maxRadius=0;
    for(let k=0;k<sides;k++){
     const a=phase+k*pmath.TWO_PI/sides,dist=r*(.85+random()*.24);
     // Separate per-face RNG leaves the site's original block layout and
     // candidate continuation stable while adding coherent rock fractures.
     const faceRandom=rngFromString(`${seed}:face:${j}:${k}`),insetM=(5+faceRandom()*24)*scale;
     const kneeM=faceM*(.20+faceRandom()*.35),kneeHeightM=h*(.51+faceRandom()*.28),setbackM=(3+faceRandom()*17)*scale;
     const lowerM=faceM*(1.05+faceRandom()*.6)+setbackM,sideShoulder=shoulderM*(.18+faceRandom()*.82),sideApron=apronM*(.85+faceRandom()*.22);
     planes.push(Object.freeze({x:pmath.cos(a),y:pmath.sin(a),distanceM:dist,insetM,kneeM,kneeHeightM,setbackM,lowerM,shoulderM:sideShoulder,apronM:sideApron}));
     maxRadius=Math.max(maxRadius,dist+lowerM+sideShoulder+sideApron);
    }
    // Every plane's support grows by the apron distance. Circumscribing the
    // regular normal fan bounds its corners even when distances are irregular.
    const blockRadius=maxRadius/pmath.cos(pmath.TWO_PI*.5/sides);
    radiusM=Math.max(radiusM,Math.sqrt(u*u+v*v)+blockRadius);
    blocks.push(Object.freeze({u,v,heightM:h,faceM,shoulderM,apronM,shoulderHeightM,planes:Object.freeze(planes)}));
   }
   radiusM+=2;
   if(features.some(f=>{const gap=(f.radiusM+radiusM+300*scale)/def.radius;return dot(center,f.center)>1-gap*gap*.5;}))return;
   // Reserve open travel corridors between distinct monuments. The bound is
   // also deliberately larger than the final rise for conservative terrain LOD.
   features.push(Object.freeze({id:`${def.id}:butte:${key}`,seed,center:Object.freeze(center),along:Object.freeze(along),across:Object.freeze(across),radiusM,
    heightM:height+radiusM*.2+64,baseM,gradeU,gradeV,blocks:Object.freeze(blocks),duneId}));
  };
  // Native sand fields provide dry-country context, but each rock monument has
  // its own finite footprint. No relief is painted over a whole dune province.
  for(const f of dunes){
   const random=rngFromString(`${def.seed}:butte-margin:${f.id}`);
   for(let i=0;i<2;i++)tryFeature(butteDirection(f,def.radius,(random()-.5)*f.halfLengthM*1.25,(i===0?-1:1)*f.halfWidthM*(.78+random()*.25)),`d${f.id.slice(f.id.lastIndexOf(':')+1)}:${i}`,f.id);
  }
  // A dry living world need not have mobile sand at every rocky outcrop. This
  // bounded fallback samples the same native climate/support, not a new biome.
  const count=384,first=(hashSeed(`${def.seed}:butte-order`)>>>0)%count,phase=rngFromString(`${def.seed}:butte-sites`)()*pmath.TWO_PI;
  for(let n=0;n<count&&features.length<limit;n++){
   const i=(first+n*173)%count,y=1-2*(i+.5)/count,a=phase+i*2.399963229728653,r=Math.sqrt(1-y*y);
   tryFeature([r*pmath.cos(a),y,r*pmath.sin(a)],`n${i}`,null);
  }
  Object.freeze(features);
  const mutable=new Map<number,ButteDef[]>(),R=def.radius;
  for(const f of features){
   const bound=f.radiusM+R*(1-R/Math.sqrt(R*R+f.radiusM*f.radiusM));
   const lo=f.center.map(v=>Math.max(0,Math.floor((v*R-bound+R)/this.cellM))),hi=f.center.map(v=>Math.min(this.gridSize-1,Math.floor((v*R+bound+R)/this.cellM)));
   for(let x=lo[0];x<=hi[0];x++)for(let y=lo[1];y<=hi[1];y++)for(let z=lo[2];z<=hi[2];z++){
    const key=this.key(x,y,z);let list=mutable.get(key);if(!list)mutable.set(key,list=[]);list.push(f);
   }
  }
  for(const[key,list]of mutable)this.cells.set(key,Object.freeze(list));
 }
 private key(x:number,y:number,z:number):number{return(x*this.gridSize+y)*this.gridSize+z}
 private candidates(dx:number,dy:number,dz:number):readonly ButteDef[]|undefined{
  const R=this.def.radius,c=this.cellM;return this.cells.get(this.key(Math.floor((dx*R+R)/c),Math.floor((dy*R+R)/c),Math.floor((dz*R+R)/c)));
 }
 hasCandidates(dx:number,dy:number,dz:number):boolean{
  if(!this.enabled)return false;const list=this.candidates(dx,dy,dz);if(!list)return false;
  for(const f of list){const facing=dx*f.center[0]+dy*f.center[1]+dz*f.center[2];if(facing<=0)continue;
   const R=this.def.radius/facing,u=(dx*f.along[0]+dy*f.along[1]+dz*f.along[2])*R,v=(dx*f.across[0]+dy*f.across[1]+dz*f.across[2])*R;
   if(u*u+v*v<f.radiusM*f.radiusM)return true;
  }return false;
 }
 sample(dx:number,dy:number,dz:number,heightM:number,temp:number,moist:number,mountain:number,plateau:number,out:ButteSample):ButteSample{
  out.heightM=heightM;out.rock=out.weight=0;const climate=duneClimate(temp,moist);
  if(!this.enabled||climate<=0||heightM<=this.seaM+25||mountain>=.26||plateau>=.12)return out;
  const list=this.candidates(dx,dy,dz);if(!list)return out;
  const eligibility=climate*smooth((heightM-this.seaM-25)/35)*(1-smooth((mountain-.10)/.16))*(1-smooth(plateau/.12));
  for(const f of list){const facing=dx*f.center[0]+dy*f.center[1]+dz*f.center[2];if(facing<=0)continue;
   const R=this.def.radius/facing,u=(dx*f.along[0]+dy*f.along[1]+dz*f.along[2])*R,v=(dx*f.across[0]+dy*f.across[1]+dz*f.across[2])*R;
   if(u*u+v*v>=f.radiusM*f.radiusM)continue;
   const p=butteProfile(f,u,v,this.scratch),delta=Math.max(0,p.heightM-heightM),weight=p.weight*eligibility;
   out.heightM=Math.max(out.heightM,heightM+delta*weight);
   out.rock=Math.max(out.rock,p.rock*smooth(delta*weight/12));out.weight=Math.max(out.weight,weight);
  }return out;
 }
}
